import {
  Box,
  Button,
  Chip,
  Drawer,
  FormHelperText,
  Stack,
  SxProps,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { IconButton, ScrollTag } from 'utoniq-ui'

import {
  EventCollectionBoxFragment,
  Maybe,
  UserCartFragment,
  UserCartStoreItemProceedEventCollectionFragment,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import {
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Cell } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { dayjs } from '@/lib/dayjs'

type ReceiveTimeModalProps = {
  pickupTime: EventCollectionBoxFragment['pickupTime']
  validMinutes: number
  collectionId: string
  collectionTitle: string
  open: boolean
  value?: ReceiveTimeValue
  onClose: () => void
  onSubmit: (values: ReceiveTimeValue) => void
  myPickupUsers:
    | {
        pickupTime: number
        items: {
          itemId: string
          count: number
          collectionId: string
        }[]
      }[]
    | undefined
  cartItems: UserCartFragment['items'] | undefined
  eventCollection: UserCartStoreItemProceedEventCollectionFragment | undefined
}

export type ReceiveTimeValue = {
  collectionId: string
  pickupDate: string
  pickupTime: string
}

const schema: UseFormProps['schema'] = o => ({
  collectionId: o.string(),
  pickupDate: o
    .string()
    .label(tLabel('storeItemPurchaseProceed.pickupDate'))
    .required(),
  pickupTime: o
    .string()
    .label(tLabel('storeItemPurchaseProceed.pickupTime'))
    .required(),
})

export const ReceiveTimeModal: Cell<ReceiveTimeModalProps> = ({
  pickupTime,
  collectionId,
  collectionTitle,
  validMinutes,
  open,
  myPickupUsers,
  cartItems,
  onClose,
  onSubmit,
  eventCollection,
}) => {
  const { t } = useTranslation('storeItemPurchaseProceed')
  const { showError } = useMessage()
  // 受取時間毎の上限を超えた時のエラー表示を行うためのフラグ
  const [errorTime, setErrorTime] = useState<string | null>(null)

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ReceiveTimeValue>({
    schema,
    defaultValues: {
      collectionId,
      pickupDate: '',
      pickupTime: '',
    },
  })

  // 入力可能な最初の日付を初期値として設定する
  // NOTE: 購入手続き中に時間外になる可能性があるのでAPIのバリデーション(10分)より5分余分にチェックする
  useEffect(() => {
    const availableFirstDateTime =
      pickupTime?.find(({ options }) => {
        const result =
          options.availableUserNumber > options.currentUserNumber &&
          dayjs(options.time)
            .add(validMinutes - 15, 'minutes')
            .unix() > dayjs().unix()

        return result
      })?.options?.time ?? ''

    setValue(
      'pickupDate',
      `${DateTime.formatDate(availableFirstDateTime, 'YYYY/MM/DD')}`
    )
    setValue(
      'pickupTime',
      `${DateTime.formatDate(availableFirstDateTime, 'HH:mm')}`
    )
  }, [])

  // 日付ごとに時間をマッピングしたもの
  const actualPickupTime = Object.entries(
    pickupTime
      ?.map(({ options }) => ({
        date: String(DateTime.formatDate(options.time, 'MM/DD')),
        time: options.time,
        availableUserNumber:
          options.availableUserNumber - options.currentUserNumber,
      }))
      .reduce((acc, { date, time, availableUserNumber }) => {
        if (!acc[date]) acc[date] = []
        acc[date].push({ time, availableUserNumber })
        return acc
      }, {} as any)
  ).map(([date, times]) => ({
    date,
    times,
  })) as {
    date: string
    times: { time: string; availableUserNumber: number }[]
  }[]

  return (
    <Drawer open={open} onClose={onClose}>
      <Stack sx={{ p: 5 }} direction="row" alignItems="center">
        <Typography
          variant="h6"
          align="center"
          sx={{ width: '100%', ml: '30px' }}
        >
          {t('pickupDateTime', '受け取り日時の選択')}
        </Typography>
        <IconButton.Close edge="end" size="small" onClick={onClose} />
      </Stack>
      <Stack sx={{ px: 5, pb: 5 }}>
        <Typography variant="h6" sx={{ pt: 2 }}>
          {collectionTitle}
        </Typography>
        <Stack sx={{ pt: 2 }}>
          <Typography variant="subtitle1">
            {t('pickupDate', '受取日')}
          </Typography>
          <ScrollTag
            value={watch('pickupDate')}
            tags={actualPickupTime.map(({ date, times }) => ({
              value: String(DateTime.formatDate(times[0].time, 'YYYY/MM/DD')),
              label: date,
              // その日の最後の開始時間+有効時間-10分を過ぎた場合は選択不可
              disabled:
                dayjs(times.slice(-1)[0].time)
                  .add(validMinutes - 10, 'minute')
                  .unix() < dayjs().unix(),
            }))}
            onClick={newValue => {
              if (newValue !== watch('pickupDate')) {
                setValue('pickupTime', '')
              }
              setValue('pickupDate', newValue)
              setErrorTime(null)
            }}
          />
          {errors.pickupDate && (
            <FormHelperText error sx={{ bgcolor: 'unset' }}>
              {errors.pickupDate.message}
            </FormHelperText>
          )}
        </Stack>
        <Stack sx={{ pt: 2 }}>
          <Typography variant="subtitle1">
            {t('pickupTime', '受取時間')}
          </Typography>

          {calcurateIsJustNow(
            eventCollection,
            dayjs(`${watch('pickupDate')} ${watch('pickupTime')}`).valueOf()
          ) && (
            <JustNowChip
              hasBubble
              sx={{ position: 'relative', left: 8, top: 2 }}
            />
          )}

          <ReceiveTimeTags
            value={watch('pickupTime')}
            tags={
              actualPickupTime
                .find(
                  ({ date }) =>
                    date === DateTime.formatDate(watch('pickupDate'), 'MM/DD')
                )
                ?.times?.map(({ time, availableUserNumber }, index) => ({
                  value: String(DateTime.formatDate(time, 'HH:mm')),
                  label: `${String(
                    DateTime.formatDate(time, 'HH:mm')
                  )} \n〜 ${String(
                    DateTime.formatDate(
                      dayjs(time).add(validMinutes, 'minute').valueOf(),
                      'HH:mm'
                    )
                  )}`,
                  // 受付開始時間+有効時間-10分を過ぎた場合は選択不可
                  disabled:
                    dayjs(time)
                      .add(validMinutes - 10, 'minutes')
                      .unix() < dayjs().unix() || !availableUserNumber,
                  availableUserNumber,
                })) ?? []
            }
            errorTime={errorTime}
            onClick={async newValue => {
              // 受け取り時間毎の購入制限チェック
              await checkPurchaseLimitPerPickup({
                collectionId,
                date: watch('pickupDate'),
                hour: newValue,
                myPickupUsers,
                purchaseLimitPerPickup: eventCollection?.purchaseLimitPerPickup,
                cartItems,
                message: t('purchaseLimitPerPickupTimeSelectError', {
                  number: eventCollection?.purchaseLimitPerPickup,
                }),
              })
                .then(() => {
                  setValue('pickupTime', newValue)
                  setErrorTime(null)
                })
                .catch(e => {
                  showError(e)
                  setValue('pickupTime', '')
                  setErrorTime(newValue)
                })
            }}
          />
          {errors.pickupTime && (
            <FormHelperText error sx={{ bgcolor: 'unset' }}>
              {errors.pickupTime.message}
            </FormHelperText>
          )}
        </Stack>
      </Stack>
      <Stack alignItems="center" sx={{ py: 5 }}>
        <Button
          size="large"
          sx={{ width: 212 }}
          onClick={handleSubmit(values => {
            onSubmit(values)
            onClose()
          })}
        >
          {t('decide', '決定する')}
        </Button>
      </Stack>
    </Drawer>
  )
}

type ReceiveTimeTagsProps = {
  tags: {
    label: string
    value: string
    disabled: boolean
    availableUserNumber: number
  }[]
  value: string
  onClick: (value: string) => void
  errorTime: string | null
}
const ReceiveTimeTags: Cell<ReceiveTimeTagsProps> = ({
  tags,
  value,
  onClick,
  errorTime,
}) => {
  const isActive = useCallback(
    (tagValue: string) => tagValue === value,
    [value]
  )

  return (
    <Stack
      flexWrap="wrap"
      sx={{
        py: 2,
        overflowY: 'auto',
        ...styleSets.hideScrollbar(),
      }}
    >
      <Stack spacing={2} direction="row">
        {tags.map((tag, index) => (
          <Chip
            key={index}
            disabled={tag.disabled}
            label={
              <>
                <Typography
                  variant="subtitle1"
                  color="inherit"
                  sx={{
                    whiteSpace: 'pre',
                    lineHeight: 1.2,
                  }}
                >
                  {tag.label}
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="inherit"
                  align="center"
                  sx={{
                    whiteSpace: 'pre',
                    lineHeight: 1.2,
                  }}
                >
                  {!tag.availableUserNumber && '×'}
                  {tag.availableUserNumber >= 1 &&
                    tag.availableUserNumber < 10 &&
                    '△'}
                  {tag.availableUserNumber >= 10 && '◯'}
                </Typography>
              </>
            }
            onClick={() => onClick(tag.value)}
            color={isActive(tag.value) ? 'primary' : 'default'}
            variant={isActive(tag.value) ? 'filled' : 'outlined'}
            sx={{
              py: '2px',
              borderRadius: 2,
              bgcolor: isActive(tag.value)
                ? 'primary.main'
                : tag.disabled
                ? 'transparent'
                : 'background.default',
              color: isActive(tag.value) ? 'background.default' : 'text.hint',
              borderColor:
                tag.value === errorTime
                  ? 'error.main'
                  : isActive(tag.value)
                  ? 'primary.main'
                  : 'grey.100',
              borderWidth: 1,
              borderStyle: 'solid',
              height: 'auto',
            }}
          />
        ))}
      </Stack>
    </Stack>
  )
}

/** 「コレクションの、受取時間ごとのアイテム合計数の制限」purchaseLimitPerPickuupをチェックする。選択した受取時間において、アイテムの所持数 + カート総数が上限を超過していればエラーを投げる */
export async function checkPurchaseLimitPerPickup({
  collectionId,
  date,
  hour,
  myPickupUsers,
  purchaseLimitPerPickup,
  cartItems,
  message,
}: {
  // 対象のコレクションID
  collectionId: string
  /** 対象の日付(YYYY/MM/DD) */
  date: string
  /** 対象の時間(HH:mm) */
  hour: string
  /** カート内アイテムのpickupUserのデータ */
  myPickupUsers: ReceiveTimeModalProps['myPickupUsers']
  /** カート内アイテムのデータ */
  cartItems: ReceiveTimeModalProps['cartItems']
  /** 受け取り時間単位の購入制限 */
  purchaseLimitPerPickup: Maybe<number> | undefined
  /** エラーメッセージ */
  message: string
}) {
  if (!purchaseLimitPerPickup) return

  // カート内のアイテムの合計値だけでの購入制限チェック
  const cartItemsCount = cartItems?.reduce((acc, crr) => {
    if (crr.item.collection?.id !== collectionId) return acc
    return acc + crr.count
  }, 0)
  // カート内アイテムの合計値が上限を超えていればエラー
  if (cartItemsCount && cartItemsCount > purchaseLimitPerPickup) {
    throw new Error(message)
  }
  if (!date || !hour) return

  // 受取時間単位での購入制限チェック
  const targetPickupTime = dayjs(`${date} ${hour}`).valueOf()
  // その受取時間で既に取得しているアイテムの数を取得
  const ownedItemsCount =
    myPickupUsers?.reduce((acc, crr) => {
      if (crr.pickupTime !== targetPickupTime) return acc
      return (
        acc +
        crr.items?.reduce(
          (acc, crr) =>
            crr.collectionId === collectionId ? acc + crr.count : acc,
          0
        )
      )
    }, 0) ?? 0

  // その時間のアイテム所有合計地とカート内アイテム数合計値が上限を超えていればエラー
  if (
    !cartItemsCount ||
    cartItemsCount + ownedItemsCount <= purchaseLimitPerPickup
  )
    return

  throw new Error(message)
}

// 与えられた時間が「今すぐ」に当てはまるか算出する
export const calcurateIsJustNow = (
  eventCollection: UserCartStoreItemProceedEventCollectionFragment | undefined,
  time?: number
) => {
  if (!time || !eventCollection || !eventCollection?.validMinutes) return false
  return (
    dayjs().valueOf() > time &&
    dayjs(time)
      .add(eventCollection.validMinutes - 10, 'minute')
      .valueOf() > dayjs().valueOf()
  )
}

// 「今すぐ受け取り」を表示するためのチップコンポーネント
export const JustNowChip: Cell<{ hasBubble?: boolean; sx?: SxProps }> = ({
  hasBubble = false,
  sx,
}) => {
  const { t } = useTranslation('storeItemPurchaseProceed')

  return (
    <Box sx={sx}>
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'like.tap',
          width: '66px',
          height: '16px',
          flexGrow: 0,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          padding: '2px 8px',
          borderRadius: '20px',
        }}
      >
        {hasBubble && (
          <Box
            sx={{
              position: 'absolute',
              width: '7px',
              height: '7px',
              transform: 'rotate(45deg)',
              bgcolor: 'like.tap',
              right: 0,
              left: 0,
              margin: 'auto',
              bottom: '-2.6px',
            }}
          />
        )}
        <Typography
          sx={{
            color: 'white',
            fontSize: '10px',
            lineHeight: 1.2,
            letterSpacing: 'normal',
          }}
        >
          {t('justNow')}
        </Typography>
      </Box>
    </Box>
  )
}
